import React, { FC, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { CircularProgress } from '@mui/material';
import { Image } from 'common/contracts';
import { EAssetType } from 'constants/enums';
import { ProjectCategory } from 'views/Projects/constants';
import * as z from 'zod';

import { Form } from 'components/ui';

import useImages from '../../../api/useImages';
import { EToasterStatus, showToast } from '../../../utils/showToast';
import Button from '../../Button/Button';
import Input from '../../Input/Input';
import { Tabs, TabsList } from '../../Tabs/Tabs';
import {
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle
} from '../../ui';
import { TabsContent } from '../../ui/tabs';

import {
  BUILTIN_MEDIA_IDS,
  EImageUploadModalTabs,
  IMAGE_UPLOAD_TABS,
  IMG_UPLOAD_FORM_SCHEMA
} from './constants';
import MediaContent from './MediaContent/MediaContent';
import ExternalImageContent from './UploadContent/ExternalImageContent';
import UploadContent from './UploadContent/UploadContent';

type ImageUploadModalProps = {
  onClose: () => void;
  onUpload: (url: string, name: string, mimeType: string) => void;
  selectedImageUrl?: string;
  forceType?: EAssetType;
  uploadOnly?: boolean;
  uploadContext?: ProjectCategory;
  hideSuccessToast?: boolean;
  'data-testid'?: string;
  fallbackPublisherId?: string;
};

const UploadImageModal: FC<ImageUploadModalProps> = ({
  onClose,
  onUpload,
  selectedImageUrl,
  forceType,
  uploadOnly,
  uploadContext,
  hideSuccessToast = false,
  'data-testid': dataTestId,
  fallbackPublisherId
}) => {
  const { publisherId } = useParams();
  const { addImage, getImages, deleteImage } = useImages(
    publisherId || fallbackPublisherId,
    uploadContext
  );

  const [selectedUrl, setSelectedUrl] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [isCopying, setIsCopying] = useState(false);

  const imageUploadTabs = uploadOnly
    ? IMAGE_UPLOAD_TABS.filter(({ id }) => id !== EImageUploadModalTabs.MEDIA)
    : IMAGE_UPLOAD_TABS;

  const initialValues = {
    type: forceType || EAssetType.GENERAL,
    mediaId: '',
    image: null,
    tab: EImageUploadModalTabs.UPLOAD as const
  };

  const imgUploadFormConfig = useForm<z.infer<typeof IMG_UPLOAD_FORM_SCHEMA>>({
    resolver: zodResolver(IMG_UPLOAD_FORM_SCHEMA),
    defaultValues: initialValues,
    mode: 'onChange'
  });

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isValid }
  } = imgUploadFormConfig;

  const watchedTab = watch('tab');
  const isSaveDisabled =
    watchedTab === EImageUploadModalTabs.MEDIA ? !selectedUrl : !isValid;

  useEffect(() => {
    setSelectedUrl(selectedImageUrl ?? '');
    if (selectedImageUrl) {
      setValue('tab', EImageUploadModalTabs.MEDIA, { shouldValidate: true });
    } else {
      setValue('tab', EImageUploadModalTabs.UPLOAD, { shouldValidate: true });
    }
  }, [selectedImageUrl, setValue]);

  const handleSelectImage = (url: string) => {
    setSelectedUrl(url);
  };

  const handleImgUploadFormSubmit = async (
    data: z.infer<typeof IMG_UPLOAD_FORM_SCHEMA>
  ) => {
    try {
      if (data.tab === 'media' && selectedUrl) {
        const selectedImage = getImages.data?.find(
          (img: Image) => img.url === selectedUrl
        );
        onUpload(
          selectedUrl,
          selectedImage?.name || '',
          selectedImage?.mimeType || ''
        );
        onClose();
        setSearchQuery('');
        return;
      }

      const trimmedImageId = (data.mediaId as string).trim();
      if (data.tab === EImageUploadModalTabs.EXTERNAL) {
        const imageResponse = await addImage.mutateAsync({
          externalImageUrl: data.externalImageUrl,
          name: trimmedImageId,
          type: data.type
        });
        handleImageUploadSuccess(
          imageResponse.data,
          'Image has been saved successfully'
        );
        return;
      }

      const file = data.image;
      const formData = new FormData();
      formData.append('image', file as any);
      formData.append('type', data.type);
      formData.append('name', trimmedImageId);

      const imageResponse = await addImage.mutateAsync(formData);
      handleImageUploadSuccess(
        imageResponse.data,
        'File has been uploaded successfully'
      );
    } catch (error: any) {
      showToast({
        message:
          error?.response?.data?.message ||
          'File has failed to upload. Please try again',
        status: EToasterStatus.ERROR
      });
    }
  };

  const handleImageUploadSuccess = (
    image: {
      url: string;
      name: string;
      mimeType: string;
    },
    successMessage: string
  ) => {
    reset(initialValues);
    onUpload(image.url, image.name, image.mimeType);
    if (!hideSuccessToast) {
      showToast({
        message: successMessage,
        status: EToasterStatus.SUCCESS
      });
    }
    onClose();
    setSearchQuery('');
  };

  const handleImageDelete = async (imageId: string) => {
    if (BUILTIN_MEDIA_IDS.has(imageId)) {
      return;
    }
    deleteImage.mutate(imageId, {
      onSuccess: () => {
        const deletedItemName =
          getImages.data.find((item: any) => {
            return item.mediaId === imageId;
          })?.name ?? '';
        getImages.refetch();
        showToast({
          message: `${deletedItemName} has been deleted`,
          status: EToasterStatus.SUCCESS
        });
      },
      onError: (data: any) => {
        const errorMsg = `${data.response.data.message} ${data.response.data.data ? data.response.data.data.join(',') : ''}`;
        showToast({
          message: errorMsg,
          status: EToasterStatus.ERROR
        });
      }
    });
  };

  if (getImages.isError) {
    showToast({
      message: 'Could not load media list from server',
      status: EToasterStatus.ERROR
    });
  }

  useEffect(() => {
    if (isCopying) {
      showToast({
        message: 'Asset URL copied to clipboard',
        status: EToasterStatus.SUCCESS
      });
      setIsCopying(false);
    }
  }, [isCopying, setIsCopying]);

  return (
    <Form {...imgUploadFormConfig}>
      <DialogPortal>
        <DialogContent className="bg-base-card overflow-hidden p-0 max-w-[646px] w-full gap-0 max-h-[745px]">
          <DialogHeader className="bg-base-muted py-4 px-6 flex flex-row items-center h-[58px] justify-between">
            <DialogTitle className="text-base-foreground text-p-l font-semibold">
              Upload Image
            </DialogTitle>
            <DialogClose
              asChild
              onClick={() => setSelectedUrl(selectedImageUrl ?? '')}
            >
              <FontAwesomeIcon
                icon="fa-regular fa-xmark"
                className="!mt-0 text-base-secondaryForeground cursor-pointer"
              />
            </DialogClose>
          </DialogHeader>

          <Tabs
            defaultValue={EImageUploadModalTabs.UPLOAD}
            value={watchedTab}
            onValueChange={(value) => {
              const nextTab = value as EImageUploadModalTabs;
              setValue('tab', nextTab, { shouldValidate: true });
              setValue(
                'isExtrenalImageUrl',
                nextTab === EImageUploadModalTabs.EXTERNAL,
                { shouldValidate: true }
              );
            }}
            className="p-6"
          >
            <div className="flex flex-col">
              <TabsList tabs={imageUploadTabs} className="mb-4 w-full" />
              <TabsContent
                value={EImageUploadModalTabs.MEDIA}
                className="mt-0 mb-4 w-full"
              >
                <Input
                  rightIcon={
                    <FontAwesomeIcon icon="fa-regular fa-magnifying-glass" />
                  }
                  placeholder="Search"
                  className="h-10 w-full"
                  isHorizontal
                  onChange={(e) => setSearchQuery(e.target.value)}
                  value={searchQuery}
                />
              </TabsContent>
            </div>

            <TabsContent value={EImageUploadModalTabs.UPLOAD} className="mt-0">
              <UploadContent
                control={control}
                watch={watch}
                setValue={setValue}
                errors={errors}
                forceType={forceType}
              />
            </TabsContent>

            <TabsContent
              value={EImageUploadModalTabs.EXTERNAL}
              className="mt-0"
            >
              <ExternalImageContent
                control={control}
                watch={watch}
                setValue={setValue}
                errors={errors}
                forceType={forceType}
              />
            </TabsContent>

            <TabsContent value={EImageUploadModalTabs.MEDIA} className="mt-0">
              {getImages.isLoading ? (
                <div className="flex items-center justify-center max-h-[600px] w-full">
                  <CircularProgress className="top-1/2 left-1/2 h-16 w-16" />
                </div>
              ) : (
                <MediaContent
                  images={getImages.data}
                  searchQuery={searchQuery}
                  forceType={forceType}
                  handleImageDelete={handleImageDelete}
                  selectedImageUrl={selectedUrl}
                  onSelectImage={handleSelectImage}
                  onDoubleClick={handleSubmit(handleImgUploadFormSubmit)}
                  setIsCopying={setIsCopying}
                />
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="bg-base-muted p-6 max-h-[84px]">
            <DialogClose asChild>
              <Button type="button" variant="link">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              disabled={isSaveDisabled}
              onClick={handleSubmit(handleImgUploadFormSubmit)}
              data-testid={dataTestId}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Form>
  );
};

export default UploadImageModal;
